import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { SECTION_ALIAS } from '../../../../constants/OneRealMortgageConstants.ts';
import { useOnboardingTracking } from '../../../../hooks/useOnboardingTracking';
import {
  AnswerApplicationQuestionRequest,
  BorrowerDto,
  FieldObject,
  FieldObjectTypeEnum,
  Question,
  StyleTemplateEnum,
} from '../../../../openapi/atlantis';
import {
  ApplicationControllerApi,
  MoneyValueCurrencyEnum,
  OnboardingResponseRoleEnum,
} from '../../../../openapi/yenta/index.ts';
import AnalyticsService from '../../../../services/AnalyticsService.ts';
import ErrorService from '../../../../services/ErrorService.ts';
import {
  addFileReferencesToApplicationThroughYenta,
  addFileReferencesToChecklist,
  uploadFileToDropbox,
} from '../../../../slices/DropboxSlice.ts';
import { processPayment } from '../../../../slices/PaymentSlice';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../slices/ToastNotificationSlice';
import { useAppDispatch, useAppSelector } from '../../../../slices/store';
import { AnalyticsEventEnum } from '../../../../types.ts';
import {
  ApplicationStepWithNewContent,
  DisplayTypeInputEnum,
  getFormQuestionAnswers,
} from '../../../../utils/ConfigDrivenFormUtils';
import Logger from '../../../../utils/Logger';
import {
  getLegacyApplicationId,
  hasCountryFieldChanged,
} from '../../../../utils/OnboardingHelpers.ts';
import { getYentaConfiguration } from '../../../../utils/OpenapiConfigurationUtils.tsx';
import { removeHtmlTagsFromString } from '../../../../utils/StringUtils.ts';
// import SponsorService from '../../../../utils/SponsorUtils';
import { useApplication } from '../../../../hooks/useApplication.ts';
import DefaultLoader from '../../../DefaultLoader.tsx';
import SimpleConfirmationModal from '../../../Modals/SimpleConfirmationModal';
import ConfigDrivenFieldsV2 from './ConfigDrivenFieldsV2.tsx';
import ConfigDrivenFormFooter from './ConfigDrivenFormFooter.tsx';

interface ConfigDrivenFormV2Props {
  config: ApplicationStepWithNewContent<Question>;
  onNext: () => Promise<void>;
  onSubmit: (formData: AnswerApplicationQuestionRequest) => Promise<void>;
  onNavigateToQuestion?: (questionId: string) => Promise<void>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
}

const ConfigDrivenFormV2: React.FC<ConfigDrivenFormV2Props> = ({
  config,
  onNext,
  onSubmit,
  onNavigateToQuestion,
  setIsSubmitting,
  isSubmitting,
}) => {
  const dispatch = useAppDispatch();
  const { applicationId } = useParams();
  const {
    auth: { authUserDetail },
    application: { currentQuestion, questionAnswers },
  } = useAppSelector((state) => state);

  const [showLicenseConfirmation, setShowLicenseConfirmation] = useState(false);
  const [showReviewConfirmation, setShowReviewConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const {
    trackApplicationFormSubmitted,
    trackSelfServeOnboardingApplicationFormSubmitted,
  } = useOnboardingTracking();

  const legacyApplicationId = authUserDetail?.onboardings?.find(
    (item) => item.legacyApplicationId,
  )?.legacyApplicationId;

  const hasTeamCompensationPlanAndTeamAgreementAddendumSignature =
    useMemo(() => {
      return !!(
        questionAnswers?.answersByQuestionAlias?.[
          SECTION_ALIAS.TEAM_COMPENSATION_PLAN
        ] &&
        questionAnswers?.answersByQuestionAlias?.[
          SECTION_ALIAS.TEAM_AGREEMENT_ADDENDUM_SIGNATURE
        ]
      );
    }, [questionAnswers?.answersByQuestionAlias]);

  // Use the useApplication hook to fetch application data
  const { data: applicationData, isLoading: isLoadingApplication } =
    useApplication(legacyApplicationId, {
      enabled: hasTeamCompensationPlanAndTeamAgreementAddendumSignature,
    });

  // Extract team invitation data
  const invitationId = useMemo(() => {
    if (!applicationData?.teamInvitation) return null;
    return (
      applicationData.teamInvitation.invitationId ||
      applicationData.teamInvitation.genericTeamInvitation ||
      null
    );
  }, [applicationData]);

  const teamId = useMemo(() => {
    return applicationData?.teamInvitation?.teamId || null;
  }, [applicationData]);

  const appId = useMemo(
    () =>
      applicationId ||
      getLegacyApplicationId(authUserDetail, OnboardingResponseRoleEnum.Agent),
    [authUserDetail, applicationId],
  );
  const checklistItemId = appId ? config?.id : config?.checklistItemId;
  const { watch } = useFormContext<AnswerApplicationQuestionRequest>();
  const { authUserId, dropboxId } = useAppSelector((state) => ({
    authUserId: state.auth.authUserDetail?.id!,
    dropboxId: state.auth.authUserDetail?.dropboxId,
  }));

  const handleApplicationFee = useCallback(
    async (formData: any): Promise<boolean> => {
      // Extract payment-related data from form
      const paymentFieldId = config?.content?.fields?.find(
        (field) => (field as any).type === 'PAYMENT_REQUEST',
      )?.id;

      if (!paymentFieldId) {
        Logger.error('Payment field not found in configuration');
        dispatch(
          showErrorToast(
            'Payment configuration error. Please contact support.',
          ),
        );
        return false;
      }

      // Get payment data from form (including pre-calculated totalAmount)
      const paymentData = formData[paymentFieldId] || {};
      const { paymentMethodId, fees, promoCode, skipped, totalAmount } =
        paymentData;
      let paymentResponse = null;

      // Process payment through Stripe
      // Check if payment is required (amount >= $0.50)
      if (totalAmount >= 0.5) {
        // Payment is required
        if (!paymentMethodId) {
          Logger.error(`Payment method required for amount: $${totalAmount}`);
          dispatch(
            showErrorToast(
              `Payment method required for amount: $${totalAmount}`,
              'Please add a payment method and try again.',
            ),
          );
          return false;
        }

        paymentResponse = await dispatch(
          processPayment(
            appId!,
            paymentMethodId,
            totalAmount,
            promoCode,
            (fees?.currency as MoneyValueCurrencyEnum) ||
              MoneyValueCurrencyEnum.Usd,
          ),
        );
      } else if (skipped || totalAmount < 0.5) {
        // Only skip payment if amount is truly below minimum threshold
        paymentResponse = await dispatch(
          processPayment(
            appId!,
            '',
            totalAmount,
            promoCode,
            (fees?.currency as MoneyValueCurrencyEnum) ||
              MoneyValueCurrencyEnum.Usd,
          ),
        );
      }

      // Return true if payment was successful, false otherwise
      return paymentResponse !== null;
    },
    [appId, config?.content?.fields, dispatch],
  );

  const handleSubmitReviewApplication = async (): Promise<boolean> => {
    try {
      // Retrieve sponsor info from localStorage
      // const sponsorInfo = SponsorService.getSponsorInfo();
      // const sponsorCode = sponsorInfo?.sponsorCode;

      await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).applyAsRealAgent({
        applicationId: appId!,
        // TODO: Uncomment this when we have a way to pass the sponsor code
        // sponsoredByCode: sponsorCode,
      });
      dispatch(showSuccessToast('Application submitted successfully!'));
      return true;
    } catch (e: any) {
      Logger.error('Failed to submit review application:', e);
      ErrorService.notifyIgnoreAuthErrors(
        'Failed to submit review application',
        e,
      );
      dispatch(
        showErrorToast(
          'Failed to submit your application. Please try again.',
          'If the issue persists, please contact support.',
        ),
      );
      return false;
    }
  };

  const handleFileUpload = useCallback(
    async (
      file: File | null,
      formData: Record<string, any>,
      singleFileField: FieldObject,
    ): Promise<boolean> => {
      try {
        // Only upload if it's a new file (has actual size, not a converted FileResponse)
        if (!!file?.name && file.size > 0 && !(file as any).isExistingFile) {
          const fileResponse = await dispatch(
            uploadFileToDropbox(
              authUserId,
              {
                id: authUserId,
                dropboxId: dropboxId,
              } as BorrowerDto,
              file,
            ),
          );

          if (!!fileResponse?.id) {
            const fileReferences = [fileResponse];
            let success = false;
            if (
              config?.content?.style?.template ===
              ('REZEN_AGENT_1' as StyleTemplateEnum)
            ) {
              success = await dispatch(
                addFileReferencesToApplicationThroughYenta(
                  appId!,
                  checklistItemId!,
                  fileReferences,
                ),
              );
            } else {
              success = await dispatch(
                addFileReferencesToChecklist(checklistItemId!, fileReferences),
              );
            }

            if (!success) {
              Logger.error('Failed to add file reference to application');
              dispatch(
                showErrorToast(
                  'Failed to attach file to application. Please try again.',
                ),
              );
              return false;
            }

            // Set the answer to true when file is successfully uploaded
            if (singleFileField.id) {
              formData[singleFileField.id] = true;
            }
          } else {
            Logger.error('File upload failed - no file ID returned');
            dispatch(showErrorToast('File upload failed. Please try again.'));
            return false;
          }
        } else if (!!file?.name && (file as any).isExistingFile) {
          // Set to true for existing files without re-uploading
          if (singleFileField.id) {
            formData[singleFileField.id] = true;
          }
        } else {
          // Set to false if no file was selected (skipped)
          if (singleFileField.id) {
            formData[singleFileField.id] = false;
          }
        }

        return true;
      } catch (e: any) {
        Logger.error('Failed to handle file upload:', e);
        ErrorService.notify('Failed to handle file upload', e);
        dispatch(showErrorToast('Failed to upload file. Please try again.'));
        return false;
      }
    },
    [
      authUserId,
      dropboxId,
      config?.content?.style?.template,
      dispatch,
      appId,
      checklistItemId,
    ],
  );

  const handleOnSubmit = async (formData: any) => {
    // Check for License confirmation
    if (
      currentQuestion?.alias?.toLowerCase().includes(SECTION_ALIAS.LICENSE) &&
      !pendingFormData
    ) {
      setPendingFormData(formData);
      setShowLicenseConfirmation(true);
      return;
    }

    // Check for Review Application confirmation
    if (
      currentQuestion?.alias
        ?.toLowerCase()
        .includes(SECTION_ALIAS.REVIEW_APPLICATION) &&
      !pendingFormData
    ) {
      setPendingFormData(formData);
      setShowReviewConfirmation(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const singleFileField = (config?.content?.fields || [])?.find(
        (field) =>
          field?.type === FieldObjectTypeEnum.FileRequest &&
          field?.fieldStyle?.displayType === DisplayTypeInputEnum.SINGLE_FILE,
      );

      if (singleFileField?.id) {
        const file = watch(singleFileField?.id as any);
        const success = await handleFileUpload(file, formData, singleFileField);
        if (!success) {
          // Don't proceed with onSubmit if file upload failed
          return;
        }
      }

      // Handle payment field if present
      let processedFormData = formData;

      if (currentQuestion?.alias === SECTION_ALIAS.APPLICATION_FEE) {
        // Get payment data from ApplicationFee component
        const paymentFieldId = config?.content?.fields?.find(
          (field) => (field as any).type === 'PAYMENT_REQUEST',
        )?.id;

        if (paymentFieldId) {
          const getPaymentData = (window as any)[
            `getPaymentData_${paymentFieldId}`
          ];
          if (getPaymentData) {
            // Get payment data including Stripe payment method
            const paymentData = await getPaymentData();

            // If paymentData is null, validation failed
            // Errors are already shown to user, just return early
            if (paymentData === null) {
              return; // Exit handleOnSubmit without reporting to Datadog
            }

            processedFormData[paymentFieldId] = paymentData;
          }
        }

        const success = await handleApplicationFee(processedFormData);
        if (!success) {
          // Don't proceed with onSubmit if payment failed
          return;
        }

        // Clear the payment data object and let populateSpecialFieldsInFormData set it to 'paid'
        if (paymentFieldId) {
          delete processedFormData[paymentFieldId];
        }
      }

      if (currentQuestion?.alias === SECTION_ALIAS.REVIEW_APPLICATION) {
        const success = await handleSubmitReviewApplication();
        if (!success) {
          // Don't proceed with onSubmit if apply-real failed
          return;
        }
        trackApplicationFormSubmitted(
          config?.sectionName || 'application_form',
          'Continue',
        );

        // Track self-serve onboarding form submission if invitation data is present
        if (invitationId && teamId) {
          try {
            trackSelfServeOnboardingApplicationFormSubmitted(
              config?.sectionName || 'application_form',
              {
                team_invite_id: invitationId,
                team_id: teamId,
                invite_method: applicationData?.teamInvitation
                  ?.genericTeamInvitation
                  ? 'link'
                  : 'email',
              },
              'team member', // Default to team member role
              'Continue',
            );
          } catch (error) {
            Logger.error(
              'Failed to track self-serve application form submitted event',
              error,
            );
          }
        }
      }

      // Skip submit only for country question with unchanged value
      const skipSubmit =
        currentQuestion?.alias === SECTION_ALIAS.COUNTRY &&
        !hasCountryFieldChanged(processedFormData, currentQuestion);

      // Submit form data unless it's an unchanged country value
      if (!skipSubmit) {
        await onSubmit(getFormQuestionAnswers(config, processedFormData, true));
      }

      // Always navigate to next and log analytics
      await onNext();

      AnalyticsService.instance().logEvent(
        AnalyticsEventEnum.QUESTION_ANSWERED,
        {
          section: removeHtmlTagsFromString(config?.sectionName!),
          question: removeHtmlTagsFromString(config?.content?.title!),
        },
      );
    } catch (e: any) {
      Logger.error('Failed to submit answer:', e);
      ErrorService.notifyIgnoreAuthErrors('Failed to submit answer', e);
      dispatch(
        showErrorToast(
          'Failed to submit the answer. Please try again in sometime',
        ),
      );
    } finally {
      setIsSubmitting(false);
      setPendingFormData(null); // Reset after submission
    }
  };

  const isFieldTypeSignature = useMemo(() => {
    return config?.content?.fields?.some(
      (field) => field.type === FieldObjectTypeEnum.SignatureRequest,
    );
  }, [config?.content?.fields]);

  if (
    hasTeamCompensationPlanAndTeamAgreementAddendumSignature &&
    isLoadingApplication
  ) {
    return <DefaultLoader />;
  }

  return (
    <React.Fragment>
      <ConfigDrivenFieldsV2
        fields={config.content.fields}
        questionAlias={config?.alias || undefined}
        isSubmitting={isSubmitting}
        onSubmit={handleOnSubmit}
        checklistItemId={checklistItemId}
        onNavigateToQuestion={onNavigateToQuestion}
      />
      {/* Footer */}
      {!isFieldTypeSignature && (
        <ConfigDrivenFormFooter
          config={config}
          onSubmit={handleOnSubmit}
          applicationId={appId}
          onNavigateToQuestion={onNavigateToQuestion}
        />
      )}

      <SimpleConfirmationModal
        isOpen={showLicenseConfirmation}
        question='Have you selected all the states/provinces where you are licensed?'
        onNo={() => {
          setShowLicenseConfirmation(false);
          setPendingFormData(null);
        }}
        onYes={async () => {
          if (pendingFormData) {
            await handleOnSubmit(pendingFormData);
          }
          setShowLicenseConfirmation(false);
        }}
        isSubmitting={isSubmitting}
        enableIOSWebViewFix
      />

      <SimpleConfirmationModal
        isOpen={showReviewConfirmation}
        question='Are you ready to submit your application?'
        onNo={() => {
          setShowReviewConfirmation(false);
          setPendingFormData(null);
        }}
        onYes={async () => {
          if (pendingFormData) {
            await handleOnSubmit(pendingFormData);
          }
          setShowReviewConfirmation(false);
        }}
        isSubmitting={isSubmitting}
        noButtonText='Not Yet'
        yesButtonText='Submit'
        enableIOSWebViewFix
      />
    </React.Fragment>
  );
};

export default ConfigDrivenFormV2;
